import React, { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import './mapFloorPlan.scss';
// import IconMapFloorPlan from './icons/IconMapFloorPlan';
// import IconExit from './icons/IconExit';
import { overrideData } from 'assets/override-data';
import {
  getSceneMapFloorPlan,
  getSceneLayoutType,
} from 'utils/mapFloorPlanHelper';

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="13"
    viewBox="0 0 18 13"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.16797 6.49984C3.16797 6.0396 3.54106 5.6665 4.0013 5.6665H14.0013C14.4615 5.6665 14.8346 6.0396 14.8346 6.49984C14.8346 6.96007 14.4615 7.33317 14.0013 7.33317H4.0013C3.54106 7.33317 3.16797 6.96007 3.16797 6.49984Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.667969 1.49984C0.667969 1.0396 1.04106 0.666504 1.5013 0.666504H16.5013C16.9615 0.666504 17.3346 1.0396 17.3346 1.49984C17.3346 1.96007 16.9615 2.33317 16.5013 2.33317H1.5013C1.04106 2.33317 0.667969 1.96007 0.667969 1.49984Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.66797 11.4998C5.66797 11.0396 6.04107 10.6665 6.5013 10.6665H11.5013C11.9615 10.6665 12.3346 11.0396 12.3346 11.4998C12.3346 11.9601 11.9615 12.3332 11.5013 12.3332H6.5013C6.04107 12.3332 5.66797 11.9601 5.66797 11.4998Z"
      fill="white"
    />
  </svg>
);

const ArrowUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="13"
    viewBox="0 0 12 13"
    fill="none"
  >
    <path
      d="M3 8L6 5L9 8"
      stroke="#F0B619"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="7"
    viewBox="0 0 10 7"
    fill="none"
  >
    <path
      d="M9 1L3.5 6.5L1 4"
      stroke="#F0B619"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MapFloorPlan = ({ currentPano, floorMaps, onSelect }) => {
  // const [isMapOpen, setIsMapOpen] = useState(true);
  // const [hoveredHotspot, setHoveredHotspot] = useState(null);
  const [selectedApartmentType, setSelectedApartmentType] = useState('Studio');
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const { scenes } = useSelector((state) => state);

  const apartmentTypes = [
    { value: 'Studio', label: 'Studio' },
    { value: '1PN', label: '1 phòng ngủ' },
    { value: '1PN+', label: '1 phòng ngủ +' },
    { value: '1PN+1', label: '1 phòng ngủ + 1' },
    { value: '2PN', label: '2 phòng ngủ' },
    { value: '2PN+', label: '2 phòng ngủ +' },
    { value: '3PN', label: '3 phòng ngủ' },
    { value: 'Duplex', label: 'Duplex' },
  ];

  // All scenes can have map floor plan
  const isOverviewScene = true;

  // Get scene-specific map floor plan configuration
  const sceneMapConfig = useMemo(() => {
    return getSceneMapFloorPlan(currentPano?._id);
  }, [currentPano]);

  // Get layout type for current scene
  const layoutType = useMemo(() => {
    return getSceneLayoutType(currentPano?._id);
  }, [currentPano]);

  // Check if current scene is in apartmentSpots
  const isApartmentScene = useMemo(() => {
    return (
      layoutType === 'apartment' ||
      overrideData.apartmentSpots.includes(currentPano?._id)
    );
  }, [currentPano, layoutType]);

  // Get map image and hotspots from scene-specific config or fallback to floorMaps
  const mapData = useMemo(() => {
    if (sceneMapConfig) {
      return {
        image: sceneMapConfig.mapImage,
        objects: sceneMapConfig.hotspots || [],
      };
    }

    // Fallback to original floorMaps logic
    if (!floorMaps || floorMaps.length === 0) return null;

    const amenitiesFloorPlan = floorMaps.find(
      (mapPlan) =>
        mapPlan.image?.name === 'Amenities' ||
        mapPlan.image?.url?.includes('amenities.webp')
    );

    return amenitiesFloorPlan;
  }, [sceneMapConfig, floorMaps]);

  // Get hotspots and apply filtering logic
  // const mapHotspots = useMemo(() => {
  //   const allHotspots = mapData?.objects || [];
  //   const { outDoorJumpSpots } = overrideData;

  //   // If using scene-specific config, ensure current scene hotspot is included
  //   if (sceneMapConfig) {
  //     const currentSceneHotspot = allHotspots.find(
  //       (hotspot) => hotspot.id === currentPano?._id
  //     );

  //     // If current scene doesn't have a hotspot in config, add it
  //     if (!currentSceneHotspot && currentPano?._id) {
  //       return [
  //         ...allHotspots,
  //         {
  //           id: currentPano._id,
  //           title: currentPano.title || 'Current Scene',
  //           coordinates: [0.5, 0.5],
  //         },
  //       ];
  //     }

  //     return allHotspots;
  //   }

  //   // Apply original filtering logic only for fallback floorMaps
  //   const isCurrentInOutdoor = outDoorJumpSpots.includes(currentPano?._id);

  //   // Filter hotspots
  //   return allHotspots.filter((hotspot) => {
  //     const isOutdoorSpot = outDoorJumpSpots.includes(hotspot.id);

  //     // show all hotspots if not an outdoor spot
  //     if (!isOutdoorSpot) return true;

  //     // show only the current one if current pano is in outdoor spots
  //     if (isCurrentInOutdoor) {
  //       return hotspot.id === currentPano._id;
  //     }

  //     // show only the first outdoor spot if current pano is not in outdoor spots
  //     return hotspot.id === outDoorJumpSpots[0];
  //   });
  // }, [mapData, currentPano, sceneMapConfig]);

  // Handle hotspot click to navigate to scene
  // const handleHotspotClick = useCallback(
  //   (hotspot) => {
  //     const targetScene = scenes.find((scene) => scene._id === hotspot.id);
  //     if (targetScene && onSelect) {
  //       onSelect(targetScene.groupId, targetScene.id);
  //     }
  //   },
  //   [scenes, onSelect]
  // );

  // Handle apartment type selection
  const handleApartmentTypeClick = useCallback(
    (apartmentType) => {
      setSelectedApartmentType(apartmentType);
      setIsMobileDropdownOpen(false);

      // Map apartment types to scene IDs
      const apartmentTypeToScene = {
        Studio: '68e78ba6fa4f187e528e4525',
        '1PN': '68e78cc2fa4f187e528e4661',
      };

      const targetSceneId = apartmentTypeToScene[apartmentType];
      if (targetSceneId && onSelect) {
        // Find target scene to get groupId
        const targetScene = scenes.find((scene) => scene._id === targetSceneId);
        if (targetScene) {
          onSelect(targetScene.groupId, targetScene.id);
        }
      }
    },
    [onSelect, scenes]
  );

  // Handle mobile dropdown toggle
  const handleMobileDropdownToggle = useCallback(() => {
    setIsMobileDropdownOpen(!isMobileDropdownOpen);
  }, [isMobileDropdownOpen]);

  // Don't render if not in overview scene or no map data
  if (!isOverviewScene || !mapData) {
    return null;
  }

  // const handleToggleMap = () => {
  //   setIsMapOpen(!isMapOpen);
  // };


  return (
    <div
      className={`map-floorplan-display ${
        isApartmentScene ? 'apartment-layout' : ''
      }`}
    >
      {/* Apartment Type Selector - Only show in apartment scenes */}
      {isApartmentScene && (
        <div className="apartment-type-selector">
          {/* Desktop/Tablet Layout */}
          <div className="apartment-type-header">Loại căn hộ</div>
          <div className="apartment-type-grid">
            <button
              className={`apartment-type-btn ${
                selectedApartmentType === 'Studio' ? 'active' : ''
              }`}
              onClick={() => handleApartmentTypeClick('Studio')}
            >
              <span className="type-name">Studio</span>
            </button>
            <button
              className={`apartment-type-btn ${
                selectedApartmentType === '1PN' ? 'active' : ''
              }`}
              onClick={() => handleApartmentTypeClick('1PN')}
            >
              <span className="type-area">1PN</span>
            </button>
            <button className="apartment-type-btn">
              <span className="type-area">1PN+</span>
            </button>
            <button className="apartment-type-btn">
              <span className="type-area">1PN+1</span>
            </button>
            <button
              className={`apartment-type-btn ${
                selectedApartmentType === '2PN' ? 'active' : ''
              }`}
              onClick={() => handleApartmentTypeClick('2PN')}
            >
              <span className="type-area">2PN</span>
            </button>
            <button className="apartment-type-btn">
              <span className="type-area">2PN+</span>
            </button>
            <button
              className={`apartment-type-btn ${
                selectedApartmentType === '3PN' ? 'active' : ''
              }`}
              onClick={() => handleApartmentTypeClick('3PN')}
            >
              <span className="type-area">3PN</span>
            </button>
            <button
              className={`apartment-type-btn ${
                selectedApartmentType === 'Duplex' ? 'active' : ''
              }`}
              onClick={() => handleApartmentTypeClick('Duplex')}
            >
              <span className="type-area">Duplex</span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Dropdown */}
      {isApartmentScene && (
        <div className="apartment-mobile-dropdown">
          <div className="mobile-dropdown-container">
            {/* Dropdown Trigger */}
            <button
              className="mobile-dropdown-trigger"
              onClick={handleMobileDropdownToggle}
            >
              <MenuIcon />
              <span className="selected-text">
                {apartmentTypes.find(
                  (type) => type.value === selectedApartmentType
                )?.label || selectedApartmentType}
              </span>
              <ArrowUpIcon />
            </button>

            {/* Dropdown Menu */}
            {isMobileDropdownOpen && (
              <div className="mobile-dropdown-menu">
                {apartmentTypes.map((type) => (
                  <button
                    key={type.value}
                    className={`mobile-dropdown-item ${
                      selectedApartmentType === type.value ? 'selected' : ''
                    }`}
                    onClick={() => handleApartmentTypeClick(type.value)}
                  >
                    <span className="item-text">{type.label}</span>
                    {selectedApartmentType === type.value && <CheckIcon />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* <div className="map-floorplan-controls">
        <button
          className={`map-control-btn ${isMapOpen ? 'active' : ''}`}
          onClick={handleToggleMap}
          title={isMapOpen ? 'Đóng' : 'Mặt bằng'}
        >
          {isMapOpen ? <IconExit /> : <IconMapFloorPlan fill="#FFFFFF" />}
        </button>
      </div> */}

      {/* {isMapOpen && (
        <div className="map-floorplan-container">
          <div className="map-floorplan-image-wrapper">
            <img
              src={mapData.image.url}
              alt={mapData.image.name || 'Floor Plan'}
              className="map-floorplan-image"
            />

            {mapHotspots.map((hotspot, index) => {
              const isActive = currentPano?._id === hotspot.id;
              const isHovered = hoveredHotspot === hotspot.id;

              return (
                <div
                  key={hotspot.id || index}
                  className={`map-hotspot ${isActive ? 'active' : ''} ${
                    isHovered ? 'hovered' : ''
                  }`}
                  style={{
                    left: `${hotspot.coordinates[0] * 100}%`,
                    top: `${hotspot.coordinates[1] * 100}%`,
                  }}
                  onClick={() => handleHotspotClick(hotspot)}
                  onMouseEnter={() => setHoveredHotspot(hotspot.id)}
                  onMouseLeave={() => setHoveredHotspot(null)}
                >
                  <div className="map-hotspot-dot" />
                  {isHovered && hotspot.title && (
                    <div className="map-hotspot-label">{hotspot.title}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )} */}
    </div>
  );
};

export default MapFloorPlan;
